import { Controller } from "@hotwired/stimulus"

const PREFERENCE_STORAGE_KEYS = {
  tier: "deepco-pref-grid-tier",
  floaters: "deepco-pref-grid-floaters",
  strikeAnimations: "deepco-pref-grid-strike-animations"
}

const SCALE_STORAGE_KEY = "deepco-ui-scale"
const DEFAULT_SCALE = 1.0
const MIN_SCALE = 0.5
const MAX_SCALE = 2.0
const SCALE_STEP = 0.01

export default class extends Controller {
  static identifier = "grid-display-prefs"
  static targets = [
    "tierBadge",
    "tierToggle",
    "floatersToggle",
    "strikeAnimationsToggle",
    "scaleSlider",
    "scaleLabel"
  ]
  static values = {
    tierDefault: { type: Boolean, default: true },
    damageFloatersDefault: { type: Boolean, default: true },
    strikeAnimationsDefault: { type: Boolean, default: true },
    scaleDefault: { type: Number, default: DEFAULT_SCALE }
  }

  connect() {
    this.initializeScale()

    this.initializePreference(
      "tier",
      this.tierToggleTargets,
      this.tierDefaultValue,
      (value) => this.updateTierBadge(value)
    )

    this.initializePreference(
      "floaters",
      this.floatersToggleTargets,
      this.damageFloatersDefaultValue,
      (value) => this.updateFloatersState(value)
    )

    this.initializePreference(
      "strikeAnimations",
      this.strikeAnimationsToggleTargets,
      this.strikeAnimationsDefaultValue,
      (value) => this.updateStrikeAnimationsState(value)
    )
  }

  toggleTier(event) {
    this.handleToggle("tier", event?.target?.checked)
  }

  toggleFloaters(event) {
    this.handleToggle("floaters", event?.target?.checked)
  }

  toggleStrikeAnimations(event) {
    this.handleToggle("strikeAnimations", event?.target?.checked)
  }

  updateScale(event) {
    const nextValue = this.normalizeScale(
      event?.target?.value ?? this.currentScale ?? this.scaleDefaultValue
    )
    this.applyScale(nextValue, { store: true })
  }

  scaleSliderTargetConnected(target) {
    const value = this.currentScale ?? this.normalizeScale(this.scaleDefaultValue)
    target.value = value
  }

  scaleLabelTargetConnected(target) {
    const value = this.currentScale ?? this.normalizeScale(this.scaleDefaultValue)
    target.textContent = this.scaleLabelText(value)
  }

  initializePreference(key, toggles, defaultValue, updater) {
    const stored = this.readPreference(key)
    const value = stored ?? Boolean(defaultValue)

    this.syncToggles(toggles, value)
    updater(value)

    if (stored == null) {
      this.persistPreference(key, value)
    }
  }

  initializeScale() {
    const stored = this.readStoredScale()
    const initial = this.normalizeScale(stored ?? this.scaleDefaultValue ?? DEFAULT_SCALE)
    this.currentScale = initial
    this.applyScale(initial, { store: stored == null })
  }

  handleToggle(key, rawValue) {
    const value = Boolean(rawValue)

    switch (key) {
      case "tier":
        this.updateTierBadge(value)
        this.syncToggles(this.tierToggleTargets, value)
        break
      case "floaters":
        this.updateFloatersState(value)
        this.syncToggles(this.floatersToggleTargets, value)
        break
      case "strikeAnimations":
        this.updateStrikeAnimationsState(value)
        this.syncToggles(this.strikeAnimationsToggleTargets, value)
        break
      default:
        return
    }

    this.persistPreference(key, value)
  }

  updateTierBadge(enabled) {
    this.tierBadgeTargets.forEach((element) => {
      element.classList.toggle("hidden", !enabled)
      element.setAttribute("aria-hidden", (!enabled).toString())
    })
  }

  updateBodyFlag(datasetKey, enabled) {
    if (!document?.body) return
    document.body.dataset[datasetKey] = enabled ? "true" : "false"
  }

  updateFloatersState(enabled) {
    this.updateBodyFlag("damageFloatersEnabled", enabled)
  }

  updateStrikeAnimationsState(enabled) {
    this.updateBodyFlag("strikeAnimationsEnabled", enabled)
  }

  applyScale(value, { store = false } = {}) {
    this.currentScale = value
    this.setDocumentScale(value)
    this.updateScaleLabel(value)
    this.syncScaleSlider(value)

    if (store) {
      this.persistScale(value)
    }
  }

  setDocumentScale(value) {
    document.documentElement.style.setProperty("--ui-scale", value)
  }

  updateScaleLabel(value) {
    if (!this.hasScaleLabelTarget) return
    this.scaleLabelTarget.textContent = this.scaleLabelText(value)
  }

  scaleLabelText(value) {
    const numeric = Number.parseFloat(value)
    if (Number.isNaN(numeric)) return "Current: 1×"

    const formatted = numeric.toFixed(2).replace(/\.?0+$/, "")
    return `Current: ${formatted}×`
  }

  syncScaleSlider(value) {
    this.scaleSliderTargets.forEach((slider) => {
      if (slider) {
        slider.value = value
      }
    })
  }

  syncToggles(toggles, value) {
    toggles.forEach((toggle) => {
      if (toggle) {
        toggle.checked = value
      }
    })
  }

  readPreference(key) {
    const storageKey = PREFERENCE_STORAGE_KEYS[key]
    if (!storageKey) return null

    try {
      const raw = window.localStorage.getItem(storageKey)
      if (raw == null) return null
      return raw === "true"
    } catch (_error) {
      return null
    }
  }

  persistPreference(key, value) {
    const storageKey = PREFERENCE_STORAGE_KEYS[key]
    if (!storageKey) return

    try {
      window.localStorage.setItem(storageKey, value ? "true" : "false")
    } catch (_error) {
      // Ignore storage limitations (e.g., private browsing)
    }
  }

  readStoredScale() {
    try {
      return window.localStorage.getItem(SCALE_STORAGE_KEY)
    } catch (_error) {
      return null
    }
  }

  persistScale(value) {
    try {
      window.localStorage.setItem(SCALE_STORAGE_KEY, value)
    } catch (_error) {
      // Ignore storage access issues (e.g., private browsing)
    }
  }

  normalizeScale(value) {
    const numeric = Number.parseFloat(value)
    if (Number.isNaN(numeric)) return DEFAULT_SCALE

    const clamped = Math.min(MAX_SCALE, Math.max(MIN_SCALE, numeric))
    const precision = Math.round(clamped / SCALE_STEP) * SCALE_STEP
    return Number.parseFloat(precision.toFixed(2))
  }
}
